import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  TextField,
  Badge,
  Chip,
} from '@mui/material';
import { Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import AccountantLayout from '../components/AccountantLayout';

const MessagesPage = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    // Simulate loading messages
    const mockMessages = [
      {
        id: 1,
        sender: 'John Smith',
        client: 'Atlas Construction Co',
        subject: 'Q2 Financial Review',
        preview: 'Could we schedule a meeting to discuss the quarterly financial statements?',
        timestamp: '2 hours ago',
        unread: true,
      },
      {
        id: 2,
        sender: 'Sarah Johnson',
        client: 'Meridian Logistics',
        subject: 'VAT Return Question',
        preview: 'I have a question about the VAT return due next month...',
        timestamp: '5 hours ago',
        unread: true,
      },
      {
        id: 3,
        sender: 'Michael Brown',
        client: 'Orchard Foods Ltd',
        subject: 'Invoice Clarification',
        preview: 'Can you help me understand item #5 on the latest invoice?',
        timestamp: 'Yesterday',
        unread: false,
      },
      {
        id: 4,
        sender: 'Emma Wilson',
        client: 'Seabreeze Properties',
        subject: 'Meeting Confirmation',
        preview: 'Confirming our meeting scheduled for July 3rd at 10 AM.',
        timestamp: '2 days ago',
        unread: false,
      },
      {
        id: 5,
        sender: 'David Lee',
        client: 'Helix Software BV',
        subject: 'Document Request',
        preview: 'Could you please send me the audit documentation for review?',
        timestamp: '3 days ago',
        unread: false,
      },
    ];
    setMessages(mockMessages);
  }, []);

  const filteredMessages = messages.filter(
    (msg) =>
      msg.sender.toLowerCase().includes(searchQuery.toLowerCase()) ||
      msg.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
      msg.client.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  return (
    <AccountantLayout>
      <Box>
        <Box className="accountant-page-header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              {t('accountant_messages')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t('client_communications')}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #653a8b 100%)',
              },
            }}
          >
            {t('new_message')}
          </Button>
        </Box>

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder={t('search_messages_placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{
              backgroundColor: 'white',
              borderRadius: '8px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
              },
            }}
          />
        </Box>

        <Paper sx={{ borderRadius: '12px', overflow: 'hidden' }}>
          <List sx={{ p: 0 }}>
            {filteredMessages.map((message, index) => (
              <React.Fragment key={message.id}>
                <ListItem
                  button
                  sx={{
                    py: 2,
                    px: 3,
                    backgroundColor: message.unread ? 'rgba(102, 126, 234, 0.04)' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(102, 126, 234, 0.08)',
                    },
                  }}
                >
                  <ListItemAvatar>
                    <Badge
                      variant="dot"
                      color="primary"
                      invisible={!message.unread}
                      overlap="circular"
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                    >
                      <Avatar
                        sx={{
                          backgroundColor: '#667eea',
                          width: 48,
                          height: 48,
                        }}
                      >
                        {getInitials(message.sender)}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: message.unread ? 600 : 400,
                          }}
                        >
                          {message.sender}
                        </Typography>
                        <Chip
                          label={message.client}
                          size="small"
                          sx={{
                            fontSize: '0.7rem',
                            height: 20,
                          }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: message.unread ? 500 : 400,
                            color: 'text.primary',
                            mb: 0.5,
                          }}
                        >
                          {message.subject}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {message.preview}
                        </Typography>
                      </Box>
                    }
                  />
                  <Typography variant="caption" color="text.secondary">
                    {message.timestamp}
                  </Typography>
                </ListItem>
                {index < filteredMessages.length - 1 && <Divider />}
              </React.Fragment>
            ))}
            {filteredMessages.length === 0 && (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  {t('no_messages_found')}
                </Typography>
              </Box>
            )}
          </List>
        </Paper>
      </Box>
    </AccountantLayout>
  );
};

export default MessagesPage;
